import { useState, useEffect } from 'react';
import Hero from './components/Hero';
import Services from './components/Services';
import About from './components/About';
import Stats from './components/Stats';
import Gallery from './components/Gallery';
import Testimonials from './components/Testimonials';
import Contact from './components/Contact';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${SITE_URL}/#localbusiness`,
  name: 'LA.RA. SERVICE SRL',
  description: 'Lavorazioni meccaniche di precisione conto terzi e commercio all\'ingrosso di ferro per l\'edilizia a Prato, Toscana.',
  url: SITE_URL,
  logo: 'https://static.readdy.ai/image/b4340cdd1673521bdb15ca49ead9cb84/e91042ba6b64d325371245c365026672.png',
  image: 'https://static.readdy.ai/image/b4340cdd1673521bdb15ca49ead9cb84/e91042ba6b64d325371245c365026672.png',
  telephone: '+390574 1640032',
  email: 'infolaraservice@gmail.com',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Prato',
    addressRegion: 'Toscana',
    addressCountry: 'IT',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 43.880634,
    longitude: 11.097033,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '18:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: 'Saturday',
      opens: '08:00',
      closes: '13:00',
    },
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Servizi LA.RA. SERVICE SRL',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Lavorazioni Meccaniche di Precisione' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Ferro per Edilizia' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Strutture Metalliche' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Supporto Tecnico' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Logistica Nazionale' } },
    ],
  },
  areaServed: {
    '@type': 'Country',
    name: 'Italia',
  },
  priceRange: '€€',
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/#webpage`,
  url: SITE_URL,
  name: 'LA.RA. SERVICE SRL Prato - Lavorazioni Meccaniche Precisione e Ferro Edilizia Toscana',
  description: 'LA.RA. SERVICE SRL a Prato, Toscana: lavorazioni meccaniche di precisione conto terzi, ferro per edilizia e strutture metalliche.',
  inLanguage: 'it-IT',
  isPartOf: { '@id': `${SITE_URL}/#website` },
  about: { '@id': `${SITE_URL}/#localbusiness` },
  dateModified: '2026-04-04',
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  url: SITE_URL,
  name: 'LA.RA. SERVICE SRL',
  inLanguage: 'it-IT',
  publisher: { '@id': `${SITE_URL}/#localbusiness` },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Dove si trova LA.RA. SERVICE SRL?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'LA.RA. SERVICE SRL ha sede operativa a Prato, Toscana (Italia). Offriamo servizi di lavorazioni meccaniche e ferro per edilizia su tutto il territorio nazionale.',
      },
    },
    {
      '@type': 'Question',
      name: 'Quali lavorazioni meccaniche offrite?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Offriamo lavorazioni meccaniche di precisione conto terzi tra cui tornitura CNC, fresatura, foratura e rettifica, con macchinari all\'avanguardia e personale altamente qualificato.',
      },
    },
    {
      '@type': 'Question',
      name: 'Fornite ferro per edilizia in tutta Italia?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Sì, commercializziamo all\'ingrosso ferro e materiali ferrosi per costruzioni edili con consegne rapide su tutto il territorio nazionale. Travi in acciaio, profilati, lamiere e tondini.',
      },
    },
    {
      '@type': 'Question',
      name: 'Come posso richiedere un preventivo?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Puoi richiedere un preventivo compilando il modulo di contatto sul nostro sito, chiamando il numero 0574 1640032 oppure scrivendo a infolaraservice@gmail.com. Siamo disponibili dal lunedì al venerdì dalle 8:00 alle 18:00 e il sabato dalle 8:00 alle 13:00.',
      },
    },
  ],
};

function injectSchema(id: string, data: object) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    (el as HTMLScriptElement).type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    injectSchema('schema-localbusiness', localBusinessSchema);
    injectSchema('schema-webpage', webPageSchema);
    injectSchema('schema-website', websiteSchema);
    injectSchema('schema-faq', faqSchema);
    return () => {
      ['schema-localbusiness', 'schema-webpage', 'schema-website', 'schema-faq'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={scrolled} />
      <Hero />
      <Services />
      <About />
      <Stats />
      <Gallery />
      <Testimonials />
      <Contact />
      <Footer />
    </div>
  );
}
